import React from 'react';
import { Row, Col, Tooltip } from 'antd';
import {
  getFlightDetailsByAircraftId,
  getSelectedFlightAircraftId
} from '../../redux/selectors/flyingPage';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAircraftFromList,
  clearSelectedFlight
} from '../../redux/reducers/aircraftReducer/thunk';
import { StatusV2 } from './AircraftStatusV2';
import { getDateFormat, getTimezone } from '../../redux/selectors/userData';
import { FlyingAircraftIcon } from './FlyingAircraftIcon';
import { FlyingSpiderTxtIcon } from './FlyingSpiderTxtIcon';
import { formatDisplayTime, formatDuration } from './utils/timeUtils';
import { SOS } from '../Flying/AircraftList/constants';
import { RED } from './styles/constants';

const FIFTEEN_MINUTES = 15 * 60 * 1000;
export const ONE_HOUR = 60 * 60 * 1000;
const minHeight = '48px';
interface CollapsedFlightDetailItemProps {
  aircraftId: string;
}

function isFlightActive(latestPointTime: number): boolean {
  if (Date.now() - latestPointTime >= FIFTEEN_MINUTES) {
    return false;
  }
  return true;
}

const UnitWrapper: React.FC<{ unit: string; activeSOS: boolean }> = ({ unit, activeSOS }) => {
  return <span style={{ color: activeSOS ? 'inherit' : '#8C8C8C' }}>{unit}</span>;
};

const CollapsedFlightDetailItem: React.FC<CollapsedFlightDetailItemProps> = ({ aircraftId }) => {
  const dispatch = useDispatch();
  const selectedAircraftId = useSelector(getSelectedFlightAircraftId);
  const flightDetails = useSelector(
    useSelector(state => getFlightDetailsByAircraftId(state, aircraftId))
  );
  const dateFormat = useSelector(getDateFormat);
  const timeFormat = 'HH:mm';
  const dateTimeFormat = `${dateFormat} ${timeFormat}`;
  const timezone = useSelector(getTimezone);
  const activeSOS = flightDetails?.aircraft.trackStatus === SOS;

  if (!flightDetails) {
    return <></>;
  }
  const latestPoint = flightDetails.track.points[flightDetails.track.points.length - 1];
  if (!latestPoint) {
    return <></>;
  }
  const formattedDepartedTime = formatDisplayTime(
    flightDetails.track.departedTime,
    timezone,
    dateTimeFormat,
    timeFormat
  );
  const formattedEndTime = formatDisplayTime(
    flightDetails.track.endTime,
    timezone,
    dateTimeFormat,
    timeFormat
  );
  const flightDuration = formatDuration(
    flightDetails.track.departedTime,
    flightDetails.track.endTime
  );
  const flightActive = isFlightActive(latestPoint.timestamp);
  const COLUMN_FLEX_STYLE = { display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' };

  const onClickHandler = () => {
    if (selectedAircraftId === aircraftId) {
      dispatch(clearSelectedFlight());
    } else {
      dispatch(selectAircraftFromList(aircraftId));
    }
  };

  return (
    <Row
      className={`flight-detail-list-item ${
        selectedAircraftId === aircraftId ? 'selected-list-item' : ''
      }`}
      onClick={onClickHandler}
      //Required for the StatusV2 component to be vertically centered
      style={{ minHeight, color: activeSOS ? RED : undefined }}
    >
      <Col span={2}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            height: minHeight,
            paddingRight: '7px'
          }}
        >
          <StatusV2 aircraftId={aircraftId} />
        </div>
      </Col>
      <Col span={22}>
        <Row>
          <Col span={14}>
            <Row style={{ fontSize: '1.1em' }}>
              {flightDetails.aircraft.registration.length > 11 ? (
                <Tooltip
                  placement="right"
                  title={flightDetails.aircraft.registration}
                  trigger="hover"
                >
                  <b>{flightDetails.aircraft.registration.substring(0, 11)}...</b>
                </Tooltip>
              ) : (
                <b>{flightDetails.aircraft.registration}</b>
              )}
            </Row>
          </Col>
          <Col offset={3} span={7}>
            <Row type="flex" justify="end" align="bottom" style={{ gap: '4px' }}>
              <Col style={COLUMN_FLEX_STYLE}>
                {flightActive && (
                  <FlyingSpiderTxtIcon
                    enabled={!!flightDetails.track.aircraft.activeDeviceConnection}
                    userDisplayName={
                      flightDetails.track.aircraft.activeDeviceConnection
                        ? flightDetails.track.aircraft.activeDeviceConnection.user?.displayName
                        : undefined
                    }
                  />
                )}
              </Col>
              <Col style={COLUMN_FLEX_STYLE}>
                <FlyingAircraftIcon aircraftId={aircraftId} />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col span={flightActive ? 4 : 8} style={{ textAlign: 'left' }}>
            {flightActive ? (
              <span>{formattedDepartedTime}</span>
            ) : (
              <span>
                {formattedDepartedTime} - {formattedEndTime}
              </span>
            )}
          </Col>
          <Col span={flightActive ? 6 : 0} style={{ textAlign: 'end' }}>
            <span>
              {latestPoint.speed} <UnitWrapper unit={latestPoint.speedUnit} activeSOS={activeSOS} />
            </span>
          </Col>
          <Col span={9} offset={flightActive ? 0 : 2} style={{ textAlign: 'end' }}>
            <span>
              {latestPoint.altitude}{' '}
              <UnitWrapper unit={`${latestPoint.altitudeUnit} AMSL`} activeSOS={activeSOS} />
            </span>
          </Col>
          <Col span={5} style={{ textAlign: 'end' }}>
            <span>
              {flightDuration.split(' ')[0]}{' '}
              <UnitWrapper unit={flightDuration.split(' ')[1]} activeSOS={activeSOS} />
            </span>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
export default CollapsedFlightDetailItem;
